/**
 * Created by Matthieu. Gestion des GIFs "merci" personnalisés par Vox (40 slots).
 * Calqué sur le flux d'upload d'image de question :
 *   sélection/dépôt fichier -> POST multipart (champ "gif") -> aperçu via GET (fetch -> blob -> objectURL).
 * Chaque action (upload / suppression) est immédiate côté serveur ; le footer ne fait que fermer.
 */
import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { toast } from "react-toastify";

import { serverService } from '../../_services/server.service';

import './ModalSpecialGifs.css';

const NB_SLOTS = 40;
const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2 Mo
const LOADING_GIF = "data:image/gif;base64,R0lGODlhEAAQAPIAAP///wAAAMLCwkJCQgAAAGJiYoKCgpKSkiH/C05FVFNDQVBFMi4wAwEAAAAh/hpDcmVhdGVkIHdpdGggYWpheGxvYWQuaW5mbwAh+QQJCgAAACwAAAAAEAAQAAADMwi63P4wyklrE2MIOggZnAdOmGYJRbExwroUmcG2LmDEwnHQLVsYOd2mBzkYDAdKa+dIAAAh+QQJCgAAACwAAAAAEAAQAAADNAi63P5OjCEgG4QMu7DmikRxQlFUYDEZIGBMRVsaqHwctXXf7WEYB4Ag1xjihkMZsiUkKhIAIfkECQoAAAAsAAAAABAAEAAAAzYIujIjK8pByJDMlFYvBoVjHA70GU7xSUJhmKtwHPAKzLO9HMaoKwJZ7Rf8AYPDDzKpZBqfvwQAIfkECQoAAAAsAAAAABAAEAAAAzMIumIlK8oyhpHsnFZfhYumCYUhDAQxRIdhHBGqRoKw0R8DYlJd8z0fMDgsGo/IpHI5TAAAIfkECQoAAAAsAAAAABAAEAAAAzIIunInK0rnZBTwGPNMgQwmdsNgXGJUlIWEuR5oWUIpz8pAEAMe6TwfwyYsGo/IpFKSAAAh+QQJCgAAACwAAAAAEAAQAAADMwi6IMKQORfjdOe82p4wGccc4CEuQradylesojEMBgsUc2G7sDX3lQGBMLAJibufbSlKAAAh+QQJCgAAACwAAAAAEAAQAAADMgi63P7wCRHZnFVdmgHu2nFwlWCI3WGc3TSWhUFGxTAUkGCbtgENBMJAEJsxgMLWzpEAACH5BAkKAAAALAAAAAAQABAAAAMyCLrc/jDKSatlQtScKdceCAjDII7HcQ4EMTCpyrCuUBjCYRgHVtqlAiB1YhiCnlsRkAAAOwAAAAAAAAAAAA==";

interface MyProps {
    show: boolean,
    onHide(): any,
    IdVox: number,
}

interface SlotState {
    uploading: boolean,
    previewUrl: string | null,
    version: number | null,
}

interface MyState {
    loading: boolean,
    dragSlot: number,   // slot actuellement survolé en drag (0 = aucun)
    slots: { [slot: number]: SlotState },
}

function buildEmptySlots(): { [slot: number]: SlotState } {
    const slots: { [slot: number]: SlotState } = {};
    for (let i = 1; i <= NB_SLOTS; i++) {
        slots[i] = { uploading: false, previewUrl: null, version: null };
    }
    return slots;
}

class ModalSpecialGifs extends React.Component<MyProps, MyState> {

    private fileInput: HTMLInputElement | null = null;
    private pendingSlot: number = 0;

    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            dragSlot: 0,
            slots: buildEmptySlots(),
        };

        this.onEntered = this.onEntered.bind(this);
        this.onExit = this.onExit.bind(this);
        this.onInputChange = this.onInputChange.bind(this);
    }

    componentWillUnmount() {
        this.revokeAllPreviews();
    }

    // ---- ouverture / fermeture du popup ----

    onEntered() {
        const IdVox = this.props.IdVox;
        if (!IdVox) return;

        // état frais à chaque ouverture (le popup charge son propre état)
        this.setState({ loading: true, slots: buildEmptySlots() });

        serverService.getVoxGifs(IdVox).then(
            (result) => {
                this.setState({ loading: false });
                if (result && result.status === "1") {
                    const filled = result.data || [];
                    filled.forEach((g) => {
                        this.updateSlot(g.Slot, { version: g.Version });
                        this.fetchPreview(g.Slot);
                    });
                } else {
                    toast.error("Impossible de charger les GIFs");
                }
            },
            () => {
                this.setState({ loading: false });
                toast.warn("Impossible de charger les GIFs");
            }
        );
    }

    onExit() {
        this.revokeAllPreviews();
        this.setState({ slots: buildEmptySlots(), dragSlot: 0 });
    }

    // ---- helpers ----

    updateSlot(slot: number, patch: Partial<SlotState>) {
        this.setState((prev) => {
            const slots = { ...prev.slots, [slot]: { ...prev.slots[slot], ...patch } };
            return { slots };
        });
    }

    revokeAllPreviews() {
        const slots = this.state.slots;
        Object.keys(slots).forEach((key) => {
            const url = slots[key].previewUrl;
            if (url) URL.revokeObjectURL(url);
        });
    }

    // aperçu authentifié : header tAuthorization -> fetch -> blob -> objectURL
    fetchPreview(slot: number) {
        serverService.getVoxGif(this.props.IdVox, slot).then(
            (res) => {
                if (!res.ok) return; // 404 = slot vide, on n'affiche rien
                res.blob().then((blob) => {
                    const url = URL.createObjectURL(blob);
                    this.updateSlot(slot, { previewUrl: url });
                });
            },
            () => { /* aperçu indisponible, on laisse le slot vide */ }
        );
    }

    // ---- sélection de fichier ----

    triggerFileSelect(slot: number) {
        this.pendingSlot = slot;
        if (this.fileInput) {
            this.fileInput.value = ""; // autorise la re-sélection du même fichier
            this.fileInput.click();
        }
    }

    onInputChange(e) {
        const file = e.target.files && e.target.files[0];
        const slot = this.pendingSlot;
        if (slot > 0) {
            this.validateAndUpload(slot, file);
        }
    }

    onDrop(slot: number, e) {
        e.preventDefault();
        this.setState({ dragSlot: 0 });
        const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        this.validateAndUpload(slot, file);
    }

    // ---- upload / suppression ----

    validateAndUpload(slot: number, file) {
        if (!file) return;

        // Validation front (le serveur revérifie)
        if (file.type !== 'image/gif') {
            toast.error("Format GIF uniquement");
            return;
        }
        if (file.size > MAX_SIZE_BYTES) {
            toast.error("Le GIF doit faire moins de 2 Mo");
            return;
        }

        this.updateSlot(slot, { uploading: true });

        const formData = new FormData();
        formData.append('gif', file);

        serverService.uploadVoxGif(this.props.IdVox, slot, formData).then(
            (result) => {
                if (result && result.status === "1") {
                    const old = this.state.slots[slot].previewUrl;
                    if (old) URL.revokeObjectURL(old);

                    // aperçu immédiat depuis le fichier local (même octets que le serveur)
                    const previewUrl = URL.createObjectURL(file);
                    const version = result.data ? result.data.Version : null;
                    this.updateSlot(slot, { uploading: false, previewUrl: previewUrl, version: version });
                    toast.success("GIF enregistré");
                } else {
                    this.updateSlot(slot, { uploading: false });
                    toast.error("Échec de l'upload");
                }
            },
            () => {
                this.updateSlot(slot, { uploading: false });
                toast.warn("Upload impossible");
            }
        );
    }

    clearSlot(slot: number) {
        serverService.clearVoxGif(this.props.IdVox, slot).then(
            (result) => {
                if (result && result.status === "1") {
                    const old = this.state.slots[slot].previewUrl;
                    if (old) URL.revokeObjectURL(old);
                    this.updateSlot(slot, { previewUrl: null, version: null });
                    toast.success("GIF supprimé");
                } else {
                    toast.error("Suppression impossible");
                }
            },
            () => {
                toast.warn("Suppression impossible");
            }
        );
    }

    // ---- rendu ----

    renderSlot(slot: number) {
        const s = this.state.slots[slot];
        const hasGif = !!s.previewUrl;
        const isDragOver = this.state.dragSlot === slot;

        let className = "gif-slot";
        if (hasGif) className += " gif-slot--filled";
        if (isDragOver) className += " gif-slot--dragover";

        return (
            <div
                key={slot}
                className={className}
                onClick={() => { if (!s.uploading) this.triggerFileSelect(slot); }}
                onDragOver={(e) => { e.preventDefault(); if (this.state.dragSlot !== slot) this.setState({ dragSlot: slot }); }}
                onDragLeave={() => { if (this.state.dragSlot === slot) this.setState({ dragSlot: 0 }); }}
                onDrop={(e) => this.onDrop(slot, e)}
                title={"Slot " + slot}
            >
                <span className="gif-slot-num">{slot}</span>

                {hasGif &&
                    <button
                        type="button"
                        className="gif-slot-clear"
                        title="Supprimer"
                        onClick={(e) => { e.stopPropagation(); this.clearSlot(slot); }}
                    >×</button>
                }

                {s.uploading
                    ? <img className="gif-slot-loading" alt="loading" src={LOADING_GIF} />
                    : hasGif
                        ? <img className="gif-slot-img" alt={"gif slot " + slot} src={s.previewUrl as string} />
                        : <div className="gif-slot-empty"><i className="fas fa-plus" /><span>GIF</span></div>
                }
            </div>
        );
    }

    render() {
        const slotsArr: number[] = [];
        for (let i = 1; i <= NB_SLOTS; i++) slotsArr.push(i);

        return (
            <Modal
                show={this.props.show}
                onHide={this.props.onHide}
                onEntered={this.onEntered}
                onExit={this.onExit}
                size="lg"
                dialogClassName="modal-90w"
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Modal.Header className="add-vox-modal-header" closeButton translate="yes">
                    <Modal.Title id="example-custom-modal-styling-title">
                        GIFs personnalisés
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="add-vox-modal-content">
                    <p className="gif-help">
                        Remplacez un ou plusieurs des 40 GIFs « merci » par un GIF personnalisé pour ce Vox.
                        Cliquez sur un emplacement ou glissez-déposez un fichier. Format GIF uniquement, moins de 2 Mo.
                    </p>

                    {this.state.loading &&
                        <div className="gif-loading-wrap">
                            <img alt="loading" src={LOADING_GIF} />
                        </div>
                    }

                    <div className="gif-grid">
                        {slotsArr.map((slot) => this.renderSlot(slot))}
                    </div>

                    <input
                        type="file"
                        accept="image/gif"
                        style={{ display: 'none' }}
                        ref={(el) => this.fileInput = el}
                        onChange={this.onInputChange}
                    />
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="success" onClick={this.props.onHide}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default ModalSpecialGifs;